import { useMemo, useState } from "react";
import {
    Box, Button, Chip, Grid, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography,
} from "@mui/material";
import { CreditCard, Search, TrendingUp } from "@mui/icons-material";
import { useDataHora } from "../../../../../core/hooks/useDataHora";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { IEstabelecimentoOrganizacao } from "../../../../interfaces/IOrganizacao";
import { VazioBox } from "../../styles";
import { IResumoOrganizacao, nomeEstabelecimento } from "../../utils";

interface IAbaAssinaturasProps {
    estabelecimentos: IEstabelecimentoOrganizacao[];
    resumo: IResumoOrganizacao;
    carregando: boolean;
    onGerenciarAssinatura: (estabelecimento: IEstabelecimentoOrganizacao) => void;
}

const CartaoIndicador = ({ rotulo, valor, detalhe }: { rotulo: string; valor: string; detalhe?: string }) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", height: "100%" }}>
        <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
            {rotulo}
        </Typography>
        <Typography variant="h5" fontWeight="bold">{valor}</Typography>
        {detalhe && <Typography variant="caption" color="text.secondary">{detalhe}</Typography>}
    </Paper>
);

export const AbaAssinaturas = ({ estabelecimentos, resumo, carregando, onGerenciarAssinatura }: IAbaAssinaturasProps) => {
    const { real } = useMoeda();
    const { dataBr } = useDataHora();
    const [busca, setBusca] = useState("");

    const linhas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const ordenadas = [...estabelecimentos].sort(
            (a, b) => nomeEstabelecimento(a).localeCompare(nomeEstabelecimento(b), "pt-BR")
        );

        if (!termo) {
            return ordenadas;
        }

        return ordenadas.filter(estabelecimento =>
            [nomeEstabelecimento(estabelecimento), estabelecimento.situacao?.planoNome]
                .some(campo => campo?.toLowerCase().includes(termo))
        );
    }, [estabelecimentos, busca]);

    if (carregando) {
        return <Skeleton variant="rounded" height={360} sx={{ borderRadius: "8px", mt: 2 }} />;
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                    <CartaoIndicador
                        rotulo="Receita mensal"
                        valor={real(resumo.receitaMensal)}
                        detalhe={`${real(resumo.receitaMensal * 12)} por ano`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <CartaoIndicador
                        rotulo="Assinaturas ativas"
                        valor={String(resumo.assinaturasAtivas)}
                        detalhe={`${resumo.total} estabelecimento(s)`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <CartaoIndicador
                        rotulo="Em período de teste"
                        valor={String(resumo.emTeste)}
                        detalhe="Ainda não entram na receita"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <CartaoIndicador
                        rotulo="Sem assinatura vigente"
                        valor={String(resumo.semAssinatura + resumo.canceladas)}
                        detalhe="Canceladas ou nunca assinadas"
                    />
                </Grid>
            </Grid>

            <TextField
                size="small"
                placeholder="Buscar por estabelecimento ou plano"
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: "text.disabled" }} /> }}
                sx={{ mt: 3, mb: 1.5, minWidth: 300, maxWidth: 420, width: "100%" }}
            />

            {!linhas.length ? (
                <VazioBox>
                    <CreditCard sx={{ fontSize: 40, color: "text.disabled" }} />
                    <Typography variant="body2" color="text.secondary">
                        {estabelecimentos.length ? "Nenhuma assinatura corresponde à busca." : "Nenhum estabelecimento para assinar."}
                    </Typography>
                </VazioBox>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "8px" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Estabelecimento</TableCell>
                                <TableCell>Plano</TableCell>
                                <TableCell align="right">Valor mensal</TableCell>
                                <TableCell align="center">Vencimento</TableCell>
                                <TableCell>Início</TableCell>
                                <TableCell>Situação</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {linhas.map(estabelecimento => {
                                const situacao = estabelecimento.situacao;
                                const ativa = situacao?.situacaoAssinatura === "ATIVA";
                                const cancelada = situacao?.situacaoAssinatura === "CANCELADA";

                                return (
                                    <TableRow key={estabelecimento.uuid} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {nomeEstabelecimento(estabelecimento)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{situacao?.planoNome ?? "—"}</TableCell>
                                        <TableCell align="right">
                                            {situacao?.valorAssinatura != null ? real(situacao.valorAssinatura) : "—"}
                                        </TableCell>
                                        <TableCell align="center">
                                            {situacao?.diaVencimento ? `dia ${situacao.diaVencimento}` : "—"}
                                        </TableCell>
                                        <TableCell>
                                            {situacao?.dataInicioAssinatura ? dataBr(situacao.dataInicioAssinatura) : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                                <Chip
                                                    size="small"
                                                    label={ativa ? "Ativa" : cancelada ? "Cancelada" : "Sem assinatura"}
                                                    color={ativa ? "success" : cancelada ? "error" : "default"}
                                                />
                                                {situacao?.freeTier && <Chip size="small" color="info" label="Teste" />}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant={ativa ? "text" : "outlined"}
                                                startIcon={ativa ? undefined : <TrendingUp fontSize="small" />}
                                                onClick={() => onGerenciarAssinatura(estabelecimento)}
                                            >
                                                {ativa ? "Gerenciar" : cancelada ? "Reativar" : "Criar"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};
